import React, { useCallback, useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Joi from 'joi';
import {
  getErrorByName,
  getMessageFromErrorResponse,
  showError,
  showSuccess,
} from '../../../../../../../Helper';
import {
  DialogComponent,
  Inputs,
  Spinner,
} from '../../../../../../../Components';
import {
  CreateVendorDocument,
  UpdateVendorDocument,
} from '../../../../../../../Services';
import LookupAutocomplete from '../../../../../../../Components/Controls/LookupAutocomplete/LookupAutocomplete';
import { ActivityFilesUploader } from '../../../../../../../Components/UploaderActivitieFileComponent/ActivityFilesUploader';
import { isEmpty } from 'lodash';

export const VendorProfileDocumentManagementDialog = ({
  vendorId,
  activeItem,
  onSave,
  isOpen,
  isOpenChanged,
  parentTranslationPath,
  isVendorDocuments,
  isVendorContact,
  otherSubmitted,
}) => {
  const { t } = useTranslation([parentTranslationPath, 'Shared']);
  const reducer = useCallback((state, action) => {
    if (action.id !== 'edit') return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [state, setState] = useReducer(reducer, {
    documentCategoryId: null,
    category: null,
    vendorId,
    files: [],
    documentName: null,
    documentRemarks: null,
  });
  const onStateChanged = (newValue) => {
    setState(newValue);
  };
  const schema = Joi.object({
    documentCategoryId: Joi.number()
      .required()
      .messages({
        'number.base': t(`category-is-required`),
        'number.empty': t(`category-is-required`),
      }),
    documentName: isVendorContact
      ? Joi.string()
          .required()
          .messages({
            'string.base': t(`document-title-is-required`),
            'string.empty': t(`document-title-is-required`),
          })
      : Joi.any(),
    files: Joi.array()
      .required()
      .min(1)
      .messages({
        'array.base': t(`please-select-at-least-one-document`),
        'array.empty': t(`please-select-at-least-one-document`),
        'array.min': t(`please-select-at-least-one-document`),
      }),
  })
    .options({
      abortEarly: false,
      allowUnknown: true,
    })
    .validate(state);
  const onSaveClicked = async (event) => {
    event.preventDefault();
    try {
      setIsSubmitted(true);
      setIsLoading(true);
      if (getErrorByName(schema, 'files').error) {
        showError(getErrorByName(schema, 'files').message);
        return;
      }
      if (schema.error) {
        showError(t('Shared:please-fix-all-errors'));
        return;
      }
      const addOrEditDocument = {
        ...state,
        fileId: state.files[0].fileId,
        fileName: state.files[0].fileName,
      };

      if (activeItem) {
        await UpdateVendorDocument(
          addOrEditDocument.vendorDocumentId,
          addOrEditDocument
        );
      } else {
        await CreateVendorDocument(addOrEditDocument);
      }

      showSuccess(t(`saved_successfully`));
      onSave();
    } catch (error) {
      const responseMessage = getMessageFromErrorResponse(error.response.data);
      showError(
        t(
          responseMessage.includes('EXISTS')
            ? responseMessage
            : 'something_went_wrong'
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeItem) {
      setState({
        id: 'edit',
        value: {
          files: [{ fileName: activeItem.fileName, uuid: activeItem.fileId }],
          documentName: activeItem.documentName,
          documentRemarks: activeItem.documentRemarks,
          documentCategoryId: activeItem.categoryId,
          vendorDocumentId: activeItem.vendorDocumentId,
          vendorId: activeItem.vendorId,
          category: {
            lookupItemName: activeItem.categoryName,
            lookupItemId: activeItem.categoryId,
          },
        },
      });
    }
  }, [activeItem]);
  return (
    <DialogComponent
      titleText={activeItem ? t(`edit-documents`) : t(`upload-documents`)}
      saveText={activeItem ? t(`save`) : t(`upload`)}
      saveType='button'
      maxWidth='md'
      disableBackdropClick
      saveIsDisabled={!state?.documentCategoryId}
      dialogContent={
        <div className='contact-profile-document-management-dialog d-flex-column-center'>
          <Spinner
            isActive={isLoading}
            isAbsolute
          />
          <div className='mb-3 ml-5 w-75'>
            <Inputs
              idRef='documentRemarksRef'
              labelValue='documentName'
              value={state.documentName}
              parentTranslationPath={parentTranslationPath}
              onInputChanged={(e) => {
                const { value } = e.target;
                setState({ id: 'documentName', value: value });
              }}
              isWithError
              isLoading={isLoading}
              isSubmitted={isSubmitted || otherSubmitted}
              helperText={getErrorByName(schema, 'documentName').message}
              error={getErrorByName(schema, 'documentName').error}
            />
          </div>
          <div className='mb-3 ml-5 w-75'>
            <LookupAutocomplete
              label='category'
              selectedValues={state.category}
              lookupName={'VendorDocumentCategory'}
              isSubmitted={isSubmitted}
              onChange={(_e, newValue) =>
                onStateChanged({
                  id: 'edit',
                  value: {
                    ...state,
                    category: newValue,
                    documentCategoryId: newValue?.lookupItemId,
                  },
                })
              }
              parentTranslationPath={parentTranslationPath}
            />
          </div>
          <div className='mb-3 ml-5 w-75'>
            <Inputs
              idRef='documentRemarksRef'
              labelValue='documentRemarks'
              multiline
              row={5}
              value={state.documentRemarks}
              stateKey='documentRemarks'
              parentTranslationPath={parentTranslationPath}
              isDeveloperDocuments={isVendorDocuments}
              onInputChanged={(e) => {
                const { value } = e.target;
                setState({ id: 'documentRemarks', value: value });
              }}
            />
          </div>
          <ActivityFilesUploader
            state={state}
            setState={setState}
            multiple={false}
            showLoading
            accept={
              'application/pdf, image/png, image/jpg, application/vnd.openxmlformats-officedocument.wordprocessingml.document, video/mp4'
            }
            isDisabled={!isEmpty(state.files)}
          />
        </div>
      }
      saveClasses='btns theme-solid  w-100 mx-2 mb-2'
      isOpen={isOpen}
      onSaveClicked={onSaveClicked}
      onCloseClicked={isOpenChanged}
      onCancelClicked={isOpenChanged}
    />
  );
};

VendorProfileDocumentManagementDialog.propTypes = {
  developerId: PropTypes.number.isRequired,
  activeItem: PropTypes.instanceOf(Object),
  onSave: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isOpenChanged: PropTypes.func.isRequired,
  parentTranslationPath: PropTypes.string.isRequired,
  translationPath: PropTypes.string.isRequired,
  isDeveloperDocuments: PropTypes.bool,
};
VendorProfileDocumentManagementDialog.defaultProps = {
  activeItem: null,
  isDeveloperDocuments: false,
};
